import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { usePageTitle } from '../../hooks/usePageTitle';
import { FileText, ExternalLink, Info, RefreshCw, PackageCheck, Ruler, Building2, AlertTriangle, Phone, ArrowRight } from 'lucide-react';
const PDF_RESOURCES = [{
  title: 'USPS Resources — Federal Register 2020 Notice',
  description: 'Notice of Changes to Postal Service Standard 4C (Vol. 85, No. 244, Dec. 18, 2020), revising the apartment parcel locker ratio.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/FederalRegistry2020.pdf?direct=1'
}, {
  title: 'Postal Code Update — Current USPS Codes Outlined (2024)',
  description: 'A plain-language outline of current USPS code expectations for multi-family rehab/replacement, 4C unit approvals, and parcel locker requirements.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/USPScodeOutline2024.pdf?direct=1'
}, {
  title: 'Postal Code Addendum — USPS Code Outline Addendum (2024)',
  description: 'An addendum clarifying replacement and renovation guidance for legacy horizontal and vertical centralized mail units.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/USPScodeoutlinedaddendum2024.pdf?direct=1'
}];
const KEY_UPDATES = [{
  icon: RefreshCw,
  text: 'Renovations and remodels should be reviewed against current USPS standards.'
}, {
  icon: AlertTriangle,
  text: 'Legacy 4B/4B+ horizontal or vertical centralized units may no longer be appropriate for full replacement or renovation projects.'
}, {
  icon: Building2,
  text: 'Current compliant centralized mailbox options are typically USPS STD 4C mailboxes or CBUs.'
}, {
  icon: PackageCheck,
  text: 'USPS parcel locker ratio changed from 1:10 to 1:5 for apartment community buildings as of the 2020 Standard 4C update.'
}, {
  icon: Ruler,
  text: 'Parcel lockers must be 15 inches high to count toward the 1:5 tenant-door-to-parcel-locker ratio.'
}];
const REACH_ROWS = [{
  type: 'USPS Mailbox',
  fMin: '28"',
  sMin: '28"',
  fMax: '67"',
  sMax: '67"'
}, {
  type: 'USPS Parcel Locker',
  fMin: '15"',
  sMin: '15"',
  fMax: '67"',
  sMax: '67"'
}, {
  type: 'ADA Mail & Parcel',
  fMin: '15"',
  sMin: '15"',
  fMax: '48"',
  sMax: '48"'
}, {
  type: 'FHA Mail & Parcel',
  fMin: '15"',
  sMin: '9"',
  fMax: '48"',
  sMax: '54"'
}];
export function USPSResources() {
  usePageTitle('USPS Resources | MidAtlantic Mailboxes');
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1878" data-mp="2b86c8akpczeg">
      <TopBar data-id="element-1879" data-mp="3vr0evobhxlwa" />
      <Header data-id="element-1880" data-mp="3vrs148j7e79w" />
      <Navigation data-id="element-1881" data-mp="3vq748c9f2c8v" />

      <main className="flex-grow" data-id="element-1882" data-mp="3vqqjfl7ommj7">
        {/* Hero */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1883" data-mp="3rp547t7odw5u">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1884" data-mp="3myop84o7i2hd">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1885" data-mp="3mx3w8jhjirlm">
              Resource Guidance
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1886" data-mp="381nya3duabjy">
              USPS Resources for Centralized Mailbox Planning
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1887" data-mp="2z197wpm0q69l">
              Guidance for multi-family replacement and renovation projects —
              covering 4C and CBU systems, parcel locker ratios, and reach
              ranges. These materials summarize current USPS standards to help
              you plan a compliant project.
            </p>
          </div>
        </section>

        {/* Intro Notice */}
        <section className="py-10 bg-brand-background" data-id="element-1888" data-mp="3v2o3dgihorti">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1889" data-mp="3h3y6a0vo6s55">
            <div className="bg-white border border-brand-border rounded-xl p-6 flex flex-col md:flex-row gap-5 items-start shadow-sm" data-id="element-1890" data-mp="3h3599rmijh07">
              <div className="w-12 h-12 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1891" data-mp="37dvf8683zo9g">
                <Info size={24} data-id="element-1892" data-mp="3c5dempuhhgsb" />
              </div>
              <div data-id="element-1893" data-mp="2ycx99ie416o8">
                <h2 className="text-lg font-bold text-brand-charcoal mb-2" data-id="element-1894" data-mp="2ydgrl07o9x2s">
                  How to use these resources
                </h2>
                <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1895" data-mp="32f1ym4xizh52">
                  These resources help owners, managers, architects, and
                  contractors understand USPS mailbox standards for centralized
                  delivery. This is resource guidance, not legal or regulatory
                  advice — final approval can depend on USPS and local
                  Postmaster review. We always recommend confirming
                  project-specific requirements with your local USPS Postmaster
                  or with MidAtlantic Mailboxes before ordering or installing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key updates at a glance */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1896" data-mp="2sok1xn0dhzce">
          <div className="max-w-7xl mx-auto px-4" data-id="element-1897" data-mp="2nrjtx6jyqmin">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-8 text-center" data-id="element-1898" data-mp="32h97605bygza">
              Key Updates at a Glance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1899" data-mp="2xjz9xybp910w">
              {KEY_UPDATES.map(({
              icon: Icon,
              text
            }) => <div key={text} className="bg-brand-background border border-brand-border rounded-xl p-6 flex flex-col" data-id="element-1900" data-mp="37glvyqfrhy4i">
                  <div className="w-11 h-11 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center mb-4" data-id="element-1901" data-mp="3jhu76ru5fs1j">
                    <Icon size={22} data-id="element-1902" data-mp="3og5eaut27og2" />
                  </div>
                  <p className="text-brand-charcoal text-sm leading-relaxed" data-id="element-1903" data-mp="3t7n6atd3ueow">
                    {text}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Download source documents */}
        <section className="py-16 bg-brand-goldWash border-b border-brand-border" data-id="element-1904" data-mp="61mh2qikiykc">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1905" data-mp="b3lme5xvc5jx">
            <div className="text-center max-w-2xl mx-auto mb-10" data-id="element-1906" data-mp="g1daeove77iu">
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-1907" data-mp="g0lroemw949z">
                Download the Source Documents
              </h2>
              <p className="text-brand-charcoal/80" data-id="element-1908" data-mp="2hrylo8nbprr9">
                View or download the original USPS reference PDFs that these
                summaries are based on.
              </p>
            </div>
            <div className="grid gap-4" data-id="element-1909" data-mp="u5uge05c2o9j">
              {PDF_RESOURCES.map(pdf => <a key={pdf.href} href={pdf.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-4 p-5 bg-white rounded-xl border border-brand-border hover:border-brand-maroon hover:shadow-sm transition-all group" data-id="element-1910" data-mp="q4iofnyzlqy6">
                  <div className="flex items-center gap-4" data-id="element-1911" data-mp="18vtbtv5i7llb">
                    <div className="w-12 h-12 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1912" data-mp="18u8g427gdwlv">
                      <FileText size={22} data-id="element-1913" data-mp="3tm0tjnndkjuw" />
                    </div>
                    <div data-id="element-1914" data-mp="3tnbxpokqftmj">
                      <h3 className="font-bold text-brand-charcoal group-hover:text-brand-maroon transition-colors" data-id="element-1915" data-mp="3omxoi37eadmx">
                        {pdf.title}
                      </h3>
                      <p className="text-sm text-brand-muted" data-id="element-1916" data-mp="1slvx80f5j6vc">
                        {pdf.description}
                      </p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-gray-400 group-hover:text-brand-maroon shrink-0" data-id="element-1917" data-mp="1in6de23p3ccg" />
                </a>)}
            </div>
          </div>
        </section>

        {/* Replacement & renovation guidance */}
        <section className="py-16 bg-white border-b border-brand-border" data-id="element-1918" data-mp="2g8uciurjc8ro">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1919" data-mp="21jg17h4efh9n">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-8" data-id="element-1920" data-mp="21in12yzy3q0h">
              Replacement & Renovation Guidance
            </h2>

            <div className="space-y-6 text-brand-muted leading-relaxed" data-id="element-1921" data-mp="2u8d8kp72ntnm">
              <div className="bg-brand-background border border-brand-border rounded-xl p-6" data-id="element-1922" data-mp="2z04yqymbfxd8">
                <h3 className="font-bold text-brand-charcoal mb-2" data-id="element-1923" data-mp="nkcxp5lz5j7h">
                  Multi-family rehab & replacements
                </h3>
                <p className="text-sm mb-3" data-id="element-1924" data-mp="iljfizvqt40z">
                  Historically, USPS approved 1:1 replacement for legacy 4B/4B+
                  horizontal units when there were no changes in layout, unit,
                  design, or location. USPS has since updated the Postal
                  Operations Manual to communicate that any remodel or
                  renovation project should comply with current standards and be
                  upgraded to 4Cs or CBUs.
                </p>
                <p className="text-sm" data-id="element-1925" data-mp="do9udwfdzeiy">
                  The 1:1 replacement allowance now applies to repair parts and
                  damaged doors that don't require full unit replacement.
                </p>
              </div>

              <div className="bg-brand-maroon/5 border border-brand-maroon/20 rounded-xl p-6" data-id="element-1926" data-mp="1p3q0kkteco3a">
                <h3 className="font-bold text-brand-maroon mb-2 flex items-center gap-2" data-id="element-1927" data-mp="1l2mma2qzz046">
                  <AlertTriangle size={18} data-id="element-1928" data-mp="1jzvyqjjj64ps" /> Legacy 4B/4B+ units &amp;
                  non-approved 4C mounts
                </h3>
                <p className="text-sm text-brand-charcoal/80 mb-3" data-id="element-1929" data-mp="1b4y5fbch0pxx">
                  Vertical and Horizontal mailboxes do NOT meet current USPS
                  requirements for centralized mail delivery. When an apartment
                  community or other multi-unit dwelling is renovated, the
                  property should replace obsolete 4B+ receptacles with current
                  USPS STD 4C mailboxes or CBU mailboxes. Renovations occurring
                  anywhere within the building (not just the mail room) may
                  prompt an upgrade to current USPS standards.
                </p>
                <p className="text-sm text-brand-charcoal/80" data-id="element-1930" data-mp="2mjlgxx4sr0gh">
                  USPS specifically states that 4C pedestal and cabinet (Depot)
                  mount units are NOT approved. The approved install method for
                  4C units is recessed in the wall. Surface mounts are not
                  specified in the bulletin; some offices use discretion, while
                  others enforce that surface mounts are not an approved install
                  method.
                  <span className="block mt-2 text-xs italic" data-id="element-1931" data-mp="33ugwv9ep4a1y">
                    Reference: Section 632.621 — USPS POM (Postal Operations
                    Manual).
                  </span>
                </p>
              </div>

              <div className="bg-brand-background border border-brand-border rounded-xl p-6" data-id="element-1932" data-mp="2b5tje006uoyy">
                <h3 className="font-bold text-brand-charcoal mb-2" data-id="element-1933" data-mp="2g3lcgxckkh4q">
                  Parcel locker requirements
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-1934" data-mp="2k3n2g98xzm46">
                  <li className="flex items-start gap-2" data-id="element-1935" data-mp="3rii32boejee7">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1936" data-mp="3vl4voeswd3cg"></span>
                    <span data-id="element-1937" data-mp="1wgdbwkpukion">
                      As of July 2020, USPS requires a minimum 1:5 tenant-door
                      to parcel-locker ratio for multi-family communities
                      (changed from the prior 1:10 ratio).
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1938" data-mp="25ep7z29hg8oh">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1939" data-mp="20j9voer4iku6"></span>
                    <span data-id="element-1940" data-mp="1csxfyhpmzjt1">
                      Parcel lockers must be 15 inches high to comply with the
                      1:5 ratio.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1941" data-mp="1mj8sutrat3c2">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1942" data-mp="1hrh0qeshupjt"></span>
                    <span data-id="element-1943" data-mp="u1mr063h1h3s">
                      The 4CADD-10 unit (a popular 100% ADA unit) does not
                      comply with the 1:5 ratio because of its smaller parcel
                      locker — it was compliant before July 2020 under the old
                      1:10 ratio. The recommended ADA unit is the 4CADD-09.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Reach & installation planning */}
        <section className="py-16 bg-brand-background border-b border-brand-border" data-id="element-1944" data-mp="2wns4uxdo80by">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1945" data-mp="3kefgntqpja3s">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-1946" data-mp="3kexid5w52gj3">
              Reach & Installation Planning
            </h2>
            <p className="text-brand-muted mb-8 text-sm" data-id="element-1947" data-mp="3agriettw7kgv">
              Minimum / maximum reach overview across USPS, ADA, and FHA
              guidelines. ADA specifies that at least 5% of mailboxes meet
              accessibility guidelines.
            </p>

            {/* Desktop / wide: table */}
            <div className="hidden md:block overflow-x-auto bg-white border border-brand-border rounded-xl shadow-sm" data-id="element-1948" data-mp="2405zbjkvulmg">
              <table className="w-full text-sm" data-id="element-1949" data-mp="2jacgmvvuy8qp">
                <thead data-id="element-1950" data-mp="2eiucjyyjfs1i">
                  <tr className="bg-brand-charcoal text-white text-left" data-id="element-1951" data-mp="2eikmkbdhtkvt">
                    <th className="px-4 py-3 font-semibold" data-id="element-1952" data-mp="2agzfx7rjy2u2">Type</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1953" data-mp="2af4tfp0mu2nv">Forward Min</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1954" data-mp="25jfnn3y0rpj4">Side Min</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1955" data-mp="328orqn6bxsox">Forward Max</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1956" data-mp="2y6tohd4u958i">Side Max</th>
                  </tr>
                </thead>
                <tbody data-id="element-1957" data-mp="2t77bn4sx9r08">
                  {REACH_ROWS.map((row, i) => <tr key={row.type} className={i % 2 === 0 ? 'bg-white' : 'bg-brand-background'} data-id="element-1958" data-mp="2o8me5d521lfh" data-mpi={i}>
                      <td className="px-4 py-3 font-semibold text-brand-charcoal" data-id="element-1959" data-mp="1ctqqj1ofk9ms">
                        {row.type}
                      </td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1960" data-mp="12r3jnu91dou5">{row.fMin}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1961" data-mp="17poibp8n40gh">{row.sMin}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1962" data-mp="1cn6hwp0rp3bt">{row.fMax}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1963" data-mp="1heg93u1ysept">{row.sMax}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-4" data-id="element-1964" data-mp="1qebm84f2yjsh">
              {REACH_ROWS.map(row => <div key={row.type} className="bg-white border border-brand-border rounded-xl shadow-sm overflow-hidden" data-id="element-1965" data-mp="1vdxz2cqzxy0r">
                  <div className="bg-brand-charcoal text-white px-4 py-2.5 font-semibold text-sm" data-id="element-1966" data-mp="106kqzxq2pyz">
                    {row.type}
                  </div>
                  <dl className="grid grid-cols-2 gap-px bg-brand-border" data-id="element-1967" data-mp="5zt5dufvr8ht">
                    <div className="bg-white px-4 py-3" data-id="element-1968" data-mp="3u50vlfda4dgs">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1969" data-mp="3p4wdjkwq3aog">
                        Forward Min
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1970" data-mp="7xj7najn91p3">
                        {row.fMin}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1971" data-mp="gwxtfwov8o39">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1972" data-mp="gvmnbq7y748z">
                        Side Min
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1973" data-mp="qniqvgsszmkl">
                        {row.sMin}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1974" data-mp="lqikt5vyejth">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1975" data-mp="zoqorxreqle5">
                        Forward Max
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1976" data-mp="1eaw2u8wsvs1r">
                        {row.fMax}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1977" data-mp="1j8xuvjc7f1pd">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1978" data-mp="15ar40n5oa1z7">
                        Side Max
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1979" data-mp="1aavo0n5sheu6">
                        {row.sMax}
                      </dd>
                    </div>
                  </dl>
                </div>)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1980" data-mp="mjzxdtd2kpoi">
              <div className="bg-white border border-brand-border rounded-xl p-6" data-id="element-1981" data-mp="ql1pzrche3hi">
                <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-1982" data-mp="qiyyt3zle4b7">
                  Planning for all specifications
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1983" data-mp="hkwx0m81b7mv">
                  <li className="flex items-start gap-2" data-id="element-1984" data-mp="hm80ekxkiddg">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1985" data-mp="x13ggk8bof68"></span>
                    <span data-id="element-1986" data-mp="s3tlyq62tdbj">
                      To meet ALL installation specifications, plan for mailbox
                      modules that fit between 28 inches and 48 inches above
                      finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1987" data-mp="e3skp9lwg3vj">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1988" data-mp="e5dh74rrxww1"></span>
                    <span data-id="element-1989" data-mp="9azfkkmiqtix">
                      Modules containing both mailboxes and parcel lockers can
                      be installed between 15 inches and 48 inches above
                      finished floor.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white border border-brand-border rounded-xl p-6" data-id="element-1990" data-mp="3mjc9nwfjf04s">
                <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-1991" data-mp="3mlf8n5yo43ln">
                  Installation height reference
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1992" data-mp="26zv55zl2f74u">
                  <li className="flex items-start gap-2" data-id="element-1993" data-mp="2727oag2k7njh">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1994" data-mp="2c0sls7qfft48"></span>
                    <span data-id="element-1995" data-mp="2fzrm1f17r1pc">
                      <strong className="text-brand-charcoal" data-id="element-1996" data-mp="2fyyo93k8fmjc">
                        Max height
                      </strong>{' '}
                      measures the center lock of the highest tenant door: 67"
                      (USPS / partial 5% ADA), 54" (FHA), or 48" (100% ADA) from
                      finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1997" data-mp="1x3rx6d3j8e9z">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1998" data-mp="10ku5pjwu06ti"></span>
                    <span data-id="element-1999" data-mp="14l4e1383j9bj">
                      <strong className="text-brand-charcoal" data-id="element-2000" data-mp="14kctcng1ew01">
                        Access door
                      </strong>{' '}
                      measures the center of the master access door lock: 36" to
                      48" above finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-2001" data-mp="l1xm6bmnq7sc">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-2002" data-mp="q0hagioq3vnv"></span>
                    <span data-id="element-2003" data-mp="3pg5lwu1c2ktx">
                      <strong className="text-brand-charcoal" data-id="element-2004" data-mp="3pgxia72r8ml9">
                        Minimum install height
                      </strong>{' '}
                      measures from the bottom shelf by compartment type: tenant
                      door 28", parcel locker 15" from finished floor.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-brand-charcoal text-center" data-id="element-2005" data-mp="3apyagy9qu22q">
          <div className="max-w-3xl mx-auto px-4" data-id="element-2006" data-mp="3ao3rhpi18k1a">
            <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-2007" data-mp="335mbr1jkc8zv">
              Planning a 4C or CBU Project?
            </h2>
            <p className="text-gray-300 mb-8" data-id="element-2008" data-mp="2y5rnl5r69xii">
              For help choosing 4C/CBU equipment or planning a compliant
              replacement, contact MidAtlantic Mailboxes. We'll help confirm
              requirements and provide a free estimate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2009" data-mp="2k8usut7xnvwc">
              <a href="tel:301-620-7585" className="bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-2010" data-mp="2fgkxuyryo5n7">
                <Phone size={18} data-id="element-2011" data-mp="21i3d99xcr99i" /> Call 301-620-7585
              </a>
              <a href="/ContactUs.html" className="bg-transparent border-2 border-white hover:bg-white hover:text-brand-charcoal text-white px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-2012" data-mp="21jekxktx515w">
                Contact & FAQs <ArrowRight size={16} data-id="element-2013" data-mp="1cx8gj3s8oe4l" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2014" data-mp="1hwuszb08tqcc" />
      <MobileStickyBar data-id="element-2015" data-mp="1hwl4xsyrdt9a" />
    </div>;
}